// Demos — approval queue for drafted demo emails + every package delivered, with open tracking.
import React, { useState, useEffect, useCallback } from 'react';
import { MonitorPlay, RefreshCw, Copy, Eye, ExternalLink, Search, X, Send, Trash2, ShieldCheck, MessageSquare, CloudOff, Sparkles, SlidersHorizontal } from 'lucide-react';
import { apiService } from '../services/apiService';
import { Chip, Btn, Spinner, useToasts, EmptyState, inputStyle, fmtDateTime, ConfirmDialog } from './ui';

const DemosTracker = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | opened | unopened | hot
  const [q, setQ] = useState('');
  const [rep, setRep] = useState('all');       // sending mailbox
  const [status, setStatus] = useState('all'); // sent | discarded | error …
  const [hosted, setHosted] = useState('all'); // hosted | offline
  const [sort, setSort] = useState('recent');  // recent | opens | company
  const [drafts, setDrafts] = useState({}); // id -> {subject, body} local edits
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type: 'approve'|'discard'|'undeploy', item}
  const [fu, setFu] = useState(null); // follow-up composer: {item, subject, body, loading, sending, engine}
  const [push, toasts] = useToasts();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, pres] = await Promise.all([apiService.getDemos(), apiService.getPendingDemos()]);
      setDeliveries((res.deliveries || []).filter((d) => d.status !== 'pending_approval'));
      setPending(pres.pending || []);
    } catch (e) {
      push(`Failed to load demos: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [push]);

  useEffect(() => { load(); }, [load]);

  const copy = (url) => {
    navigator.clipboard?.writeText(url);
    push('Link copied');
  };

  const draftFor = (p) => drafts[p.id] || { subject: p.subject || '', body: p.body || '' };
  const setDraft = (p, patch) => setDrafts((prev) => ({ ...prev, [p.id]: { ...draftFor(p), ...patch } }));

  const doApprove = async (p) => {
    const d = draftFor(p);
    setBusyId(p.id);
    try {
      const res = await apiService.approveDemo(p.id, d.subject, d.body);
      if (res.status === 'success') {
        push(`Sent to ${p.lead_email} from ${p.sent_via}`);
        setDrafts((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
        load();
      } else {
        push(res.message || 'Send failed', 'error');
      }
    } catch (e) {
      push(`Send failed: ${e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const doDiscard = async (p) => {
    setBusyId(p.id);
    try {
      const res = await apiService.discardDemo(p.id);
      if (res.status === 'success') {
        push('Discarded — nothing was sent');
        load();
      } else {
        push(res.message || 'Discard failed', 'error');
      }
    } catch (e) {
      push(`Discard failed: ${e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  /* ── follow-up straight from the demo row (AI draft, sent by you) ── */
  const openFollowUp = async (d) => {
    setFu({ item: d, subject: '', body: '', loading: true });
    try {
      const res = await apiService.generateFollowup(d.lead_email);
      if (res.status === 'success') {
        setFu({ item: d, subject: res.subject || `Following up — ${d.company || ''}`.trim(), body: res.body || '', loading: false, engine: res.stage || res.engine });
      } else {
        setFu({ item: d, subject: `Following up — ${d.company || ''}`.trim(), body: '', loading: false });
        push(res.error || 'The assistant could not draft this one — write it yourself below', 'error');
      }
    } catch (e) {
      setFu({ item: d, subject: `Following up — ${d.company || ''}`.trim(), body: '', loading: false });
      push(`Draft failed: ${e.message}`, 'error');
    }
  };

  const sendFollowUp = async () => {
    if (!fu || !fu.body.trim()) { push('Write something first', 'error'); return; }
    setFu((f) => ({ ...f, sending: true }));
    try {
      const res = await apiService.sendFollowup(fu.item.lead_email, fu.subject, fu.body);
      if (res.status === 'success') {
        push(`Follow-up sent to ${fu.item.lead_email} from ${res.sent_via}`);
        setFu(null);
        load();
      } else {
        push(res.error || 'Send failed', 'error');
        setFu((f) => ({ ...f, sending: false }));
      }
    } catch (e) {
      push(`Send failed: ${e.message}`, 'error');
      setFu((f) => ({ ...f, sending: false }));
    }
  };

  const doUndeploy = async (d) => {
    setBusyId(d.id);
    try {
      const res = await apiService.undeployDemo(d.id);
      if (res.status === 'success') {
        push(`${res.company || 'Demo'} taken offline — ${res.freed_kb} KB freed${res.drive_trashed ? ', Drive package trashed' : ''}`);
        load();
      } else {
        push(res.error || 'Undeploy failed', 'error');
      }
    } catch (e) {
      push(`Undeploy failed: ${e.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const needle = q.trim().toLowerCase();
  const reps = [...new Set(deliveries.map((d) => d.sent_via).filter(Boolean))].sort();
  const statuses = [...new Set(deliveries.map((d) => d.status).filter(Boolean))].sort();
  const shortRep = (m) => (m || '').replace('@writenowagency.co.za', '@wna').replace('.writenowagency@gmail.com', '@gmail');
  const filtersOn = (filter !== 'all') + (rep !== 'all') + (status !== 'all') + (hosted !== 'all') + (needle ? 1 : 0);
  const clearFilters = () => { setFilter('all'); setRep('all'); setStatus('all'); setHosted('all'); setQ(''); };

  const shown = deliveries.filter((d) => {
    if (filter === 'opened' && !(d.open_count > 0)) return false;
    if (filter === 'unopened' && d.open_count > 0) return false;
    if (filter === 'hot' && !(d.open_count >= 3)) return false;
    if (rep !== 'all' && d.sent_via !== rep) return false;
    if (status !== 'all' && d.status !== status) return false;
    if (hosted === 'hosted' && !d.deployed) return false;
    if (hosted === 'offline' && d.deployed) return false;
    if (!needle) return true;
    const hay = [d.company, d.lead_email, d.folder, d.sent_via, d.status].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(needle);
  }).sort((a, b) => {
    if (sort === 'opens') return (b.open_count || 0) - (a.open_count || 0);
    if (sort === 'company') return (a.company || '').localeCompare(b.company || '');
    return 0; // 'recent' — the API already returns newest first
  });
  const openedCount = deliveries.filter((d) => d.open_count > 0).length;
  const selStyle = { ...inputStyle, padding: '7px 10px', fontSize: 12.5, width: 'auto', minWidth: 0 };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: 'var(--c-text)' }}>Demos</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginTop: 3 }}>
            {pending.length > 0 ? `${pending.length} awaiting your approval · ` : ''}
            {needle ? shown.length + ' of ' : ''}{deliveries.length} delivered · {openedCount} opened by the lead
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', minWidth: 220 }}>
            <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search company, email, rep…"
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', paddingLeft: 32, paddingRight: q ? 30 : 12, padding: '8px 12px 8px 32px', fontSize: 13 }}
            />
            {q && (
              <button onClick={() => setQ('')} title="Clear" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--c-text-3)', cursor: 'pointer', padding: 2 }}>
                <X size={13} />
              </button>
            )}
          </div>
          {[['all', 'all'], ['opened', 'opened'], ['unopened', 'unopened'], ['hot', 'hot · 3+ views']].map(([f, label]) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '7px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
                fontFamily: "'Inter', sans-serif", cursor: 'pointer', whiteSpace: 'nowrap',
                border: filter === f ? '1px solid var(--c-border-bright)' : '1px solid var(--c-border)',
                background: filter === f ? 'var(--c-primary-glow)' : 'transparent',
                color: filter === f ? 'var(--c-primary)' : 'var(--c-text-2)',
              }}
            >
              {label}
            </button>
          ))}
          <Btn small onClick={load}><RefreshCw size={13} /></Btn>
        </div>
      </div>

      {/* ── filter bar ───────────────────────────────────────────────────── */}
      {!loading && deliveries.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <SlidersHorizontal size={14} style={{ color: 'var(--c-text-3)' }} />
          <select value={rep} onChange={(e) => setRep(e.target.value)} style={selStyle} title="Filter by the mailbox it was sent from">
            <option value="all">Any mailbox</option>
            {reps.map((r) => <option key={r} value={r}>{shortRep(r)}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={selStyle} title="Filter by delivery status">
            <option value="all">Any status</option>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={hosted} onChange={(e) => setHosted(e.target.value)} style={selStyle} title="Filter by whether the demo is still hosted">
            <option value="all">Hosted &amp; offline</option>
            <option value="hosted">Still hosted</option>
            <option value="offline">Taken offline</option>
          </select>
          <span style={{ width: 1, height: 20, background: 'var(--c-border)', margin: '0 2px' }} />
          <select value={sort} onChange={(e) => setSort(e.target.value)} style={selStyle} title="Sort the list">
            <option value="recent">Newest first</option>
            <option value="opens">Most opened first</option>
            <option value="company">Company A–Z</option>
          </select>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>
            {shown.length === deliveries.length ? `${deliveries.length} demos` : `${shown.length} of ${deliveries.length}`}
          </span>
          {filtersOn > 0 && (
            <Btn small onClick={clearFilters} title="Clear all filters"><X size={12} /> Clear</Btn>
          )}
        </div>
      )}

      {loading && <div style={{ padding: 60, textAlign: 'center' }}><Spinner size={24} /></div>}

      {/* ── Awaiting approval queue ─────────────────────────────────────── */}
      {!loading && pending.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <ShieldCheck size={16} style={{ color: 'var(--c-accent)' }} />
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15.5, fontWeight: 700, color: 'var(--c-text)' }}>
              Awaiting your approval
            </div>
            <Chip tone="yellow">{pending.length}</Chip>
          </div>
          <div style={{ display: 'grid', gap: 14 }}>
            {pending.map((p) => {
              const d = draftFor(p);
              const busy = busyId === p.id;
              return (
                <div key={p.id} style={{ background: 'var(--c-bg-1)', border: '1px solid var(--c-border-bright)', borderRadius: 14, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--c-text)' }}>{p.company || p.folder}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginTop: 3 }}>
                        To <b>{p.lead_email}</b> · from {p.sent_via || 'default mailbox'} · drafted {fmtDateTime(p.created_at)}
                      </div>
                      {p.prior_delivery && (
                        <div style={{ marginTop: 6 }}>
                          <Chip tone="red">
                            ⚠ already received a demo {fmtDateTime(p.prior_delivery.sent_at)} · {p.prior_delivery.open_count} view{p.prior_delivery.open_count === 1 ? '' : 's'} — possible duplicate
                          </Chip>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      {p.demo_url && (
                        <a href={p.demo_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)', fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          preview demo <ExternalLink size={11} />
                        </a>
                      )}
                      {p.proposal_url && (
                        <a href={p.proposal_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-accent)', fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          preview proposal <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  </div>
                  <input
                    value={d.subject}
                    onChange={(e) => setDraft(p, { subject: e.target.value })}
                    placeholder="Subject"
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', fontSize: 13, fontWeight: 600, marginBottom: 8 }}
                  />
                  <textarea
                    value={d.body}
                    onChange={(e) => setDraft(p, { body: e.target.value })}
                    rows={Math.min(18, Math.max(8, (d.body || '').split('\n').length + 1))}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', fontSize: 13, lineHeight: 1.55, resize: 'vertical', fontFamily: "'Inter', sans-serif" }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Btn small tone="danger" disabled={busy} onClick={() => setConfirm({ type: 'discard', item: p })}>
                      <Trash2 size={13} /> Discard
                    </Btn>
                    <Btn small tone="primary" disabled={busy || !d.subject.trim() || !d.body.trim()} onClick={() => setConfirm({ type: 'approve', item: p })}>
                      {busy ? <Spinner size={13} /> : <Send size={13} />} Approve &amp; send
                    </Btn>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && shown.length === 0 && pending.length === 0 && (
        <EmptyState icon={MonitorPlay} title={filtersOn ? "No demos match these filters" : "No demos here yet"} sub={filtersOn ? "Widen the filters, or press Clear to see all of them." : "Drafted demo emails appear here for your approval before anything is sent."} />
      )}

      {!loading && shown.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {shown.map((d, i) => {
            const busy = busyId === d.id;
            return (
              <div key={i} style={{
                background: 'var(--c-bg-1)', border: '1px solid var(--c-border)', borderRadius: 12,
                padding: '13px 15px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
              }}>
                {/* who it went to */}
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--c-text)' }}>{d.company || d.folder}</span>
                    {d.open_count > 0
                      ? <Chip tone="green"><Eye size={11} /> {d.open_count}</Chip>
                      : <Chip tone="gray">no views</Chip>}
                    {d.status !== 'sent' && <Chip tone={d.status === 'error' ? 'red' : 'yellow'}>{d.status}</Chip>}
                    {!d.deployed && <Chip tone="yellow"><CloudOff size={10} /> offline</Chip>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.lead_email}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 2 }}>
                    {fmtDateTime(d.sent_at)}
                    {d.sent_via ? ` · ${d.sent_via.replace('@writenowagency.co.za', '@wna').replace('.writenowagency@gmail.com', '@gmail')}` : ''}
                    {d.open_count > 0 && d.last_opened_at ? ` · last opened ${fmtDateTime(d.last_opened_at)}` : ''}
                    {d.deployed ? ` · ${d.size_kb} KB hosted` : d.undeployed_at ? ` · removed ${fmtDateTime(d.undeployed_at)}` : ''}
                  </div>
                  {d.error && <div style={{ fontSize: 11, color: 'var(--c-red)', marginTop: 3 }}>{d.error}</div>}
                </div>

                {/* links */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                  {d.demo_url && d.deployed && (
                    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
                      <a href={d.demo_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)', fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        demo <ExternalLink size={11} />
                      </a>
                      <button onClick={() => copy(d.demo_url)} title="Copy demo link" style={{ background: 'none', border: 'none', color: 'var(--c-text-3)', cursor: 'pointer', padding: 2 }}>
                        <Copy size={12} />
                      </button>
                    </span>
                  )}
                  {d.proposal_url && d.deployed && (
                    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
                      <a href={d.proposal_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-accent)', fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        proposal <ExternalLink size={11} />
                      </a>
                      <button onClick={() => copy(d.proposal_url)} title="Copy proposal link" style={{ background: 'none', border: 'none', color: 'var(--c-text-3)', cursor: 'pointer', padding: 2 }}>
                        <Copy size={12} />
                      </button>
                    </span>
                  )}
                </div>

                {/* actions */}
                <div style={{ display: 'flex', gap: 7, flexShrink: 0, marginLeft: 'auto' }}>
                  <Btn small disabled={busy} onClick={() => openFollowUp(d)} title="AI-draft a follow-up, CC'd to the domain mailbox, sent by you">
                    <MessageSquare size={13} /> Follow up
                  </Btn>
                  {d.deployed && (
                    <Btn small tone="danger" disabled={busy} onClick={() => setConfirm({ type: 'undeploy', item: d })} title="Delete the hosted demo and proposal, and clean up">
                      <CloudOff size={13} /> Undeploy
                    </Btn>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.type === 'approve' ? 'Send this demo email?'
          : confirm?.type === 'undeploy' ? 'Take this demo offline?'
          : 'Discard this draft?'}
        body={confirm?.type === 'approve'
          ? `This sends the email above to ${confirm?.item?.lead_email} from ${confirm?.item?.sent_via || 'the default mailbox'}.`
          : confirm?.type === 'undeploy'
          ? `The hosted demo and proposal for ${confirm?.item?.company || confirm?.item?.lead_email} (${confirm?.item?.size_kb} KB) will be deleted from the server, and the Drive package trashed if Drive access allows it. Their old links will stop working. The delivery record and open history are kept, so the dispatcher can never re-send this package.`
          : `The drafted email for ${confirm?.item?.company || confirm?.item?.lead_email} will be discarded and never sent. The demo link stays hosted.`}
        confirmLabel={confirm?.type === 'approve' ? 'Send it' : confirm?.type === 'undeploy' ? 'Undeploy & clean up' : 'Discard'}
        danger={confirm?.type !== 'approve'}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          const c = confirm; setConfirm(null);
          if (!c) return;
          if (c.type === 'approve') doApprove(c.item);
          else if (c.type === 'undeploy') doUndeploy(c.item);
          else doDiscard(c.item);
        }}
      />

      {fu && (
        <div onMouseDown={(e) => { if (e.target === e.currentTarget && !fu.sending) setFu(null); }}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 14px', overflowY: 'auto' }}>
          <div style={{ background: 'var(--c-bg-1)', border: '1px solid var(--c-border)', borderRadius: 14, width: 'min(620px, 96vw)', padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <MessageSquare size={16} style={{ color: 'var(--c-primary)' }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-text)' }}>Follow up with {fu.item.company || fu.item.lead_email}</div>
              <span style={{ flex: 1 }} />
              <button onClick={() => !fu.sending && setFu(null)} style={{ background: 'none', border: 'none', color: 'var(--c-text-3)', cursor: 'pointer' }}><X size={16} /></button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginBottom: 14 }}>
              To {fu.item.lead_email} · sent from {fu.item.sent_via || 'the owning mailbox'} · the domain mailbox is CC'd so replies reach it
              {fu.item.open_count > 0 && ` · they opened the demo ${fu.item.open_count}×`}
            </div>

            {fu.loading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '26px 0', color: 'var(--c-text-2)', fontSize: 13 }}>
                <Spinner size={14} /> The assistant is reading the conversation and the demo engagement…
              </div>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>Subject</label>
                <input value={fu.subject} onChange={(e) => setFu((f) => ({ ...f, subject: e.target.value }))} style={{ ...inputStyle, marginBottom: 12 }} />
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>
                  Message {fu.engine && <span style={{ color: 'var(--c-accent)', textTransform: 'none', letterSpacing: 0 }}>· <Sparkles size={10} /> {fu.engine}</span>}
                </label>
                <textarea value={fu.body} onChange={(e) => setFu((f) => ({ ...f, body: e.target.value }))} rows={12}
                          style={{ ...inputStyle, fontSize: 13, lineHeight: 1.55, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
                  <Btn onClick={() => openFollowUp(fu.item)} disabled={fu.sending}><RefreshCw size={12} /> Redraft</Btn>
                  <span style={{ flex: 1 }} />
                  <Btn onClick={() => setFu(null)} disabled={fu.sending}>Cancel</Btn>
                  <Btn tone="primary" onClick={sendFollowUp} disabled={fu.sending || !fu.body.trim()}>
                    {fu.sending ? <><Spinner size={12} /> Sending…</> : <><Send size={12} /> Send follow-up</>}
                  </Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {toasts}
    </div>
  );
};

export default DemosTracker;
